// Camera-domain events / recordings client.
//
// Backed by the BFF endpoints:
//   - GET /api/cameras/{doorbot_id}/events  (per-camera; date range)
//   - POST /api/events:search               (multi-camera; explicit doorbot list)
//
// The Recordings page uses the multi-camera search variant so it can render
// a fleet-wide list without N round trips. CameraDetail uses the per-camera
// variant when it needs to render an incident sidebar for a single device.
//
// AVA today returns `start_ms=0` and most metadata fields as null for
// historical events; pages that surface timestamps fall back to
// `created_at` and finally to "Unknown".

import { api } from "./client";

export interface CameraEvent {
  ding_id: string;
  doorbot_id: string;
  location_id?: string | null;
  kind?: string | null;
  created_at?: string | null;
  start_ms: number;
  duration_seconds: number;
  has_recording: boolean;
  e2ee_encrypted: boolean;
  favorite: boolean;
  person_detected: boolean;
  snapshot_url?: string | null;
}

export interface EventListResponse {
  items: CameraEvent[];
  next_cursor: string | null;
}

export interface EventSearchResponse {
  items: CameraEvent[];
  /**
   * Per-id authorization rejections. Matches BFF contract; pages can choose
   * to surface or ignore.
   */
  denied: Array<{ id: string; reason: string }>;
  next_cursor: string | null;
}

export interface ListEventsOptions {
  /** ISO-8601 start (inclusive). */
  start?: string;
  /** ISO-8601 end (exclusive). */
  end?: string;
  limit?: number;
  cursor?: string;
  signal?: AbortSignal;
}

export interface EventSearchRequest {
  doorbot_ids: string[];
  start: string;
  end: string;
  limit?: number;
  cursor?: string;
}

export const eventsClient = {
  /** GET /api/cameras/{doorbotId}/events?start&end&limit&cursor */
  async listForCamera(
    doorbotId: string,
    opts: ListEventsOptions = {},
  ): Promise<EventListResponse> {
    const params = new URLSearchParams();
    if (opts.start) params.set("start", opts.start);
    if (opts.end) params.set("end", opts.end);
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts.cursor) params.set("cursor", opts.cursor);
    const qs = params.toString();
    const path =
      qs.length === 0
        ? `/api/cameras/${encodeURIComponent(doorbotId)}/events`
        : `/api/cameras/${encodeURIComponent(doorbotId)}/events?${qs}`;
    return api.get<EventListResponse>(path, { signal: opts.signal });
  },

  /**
   * POST /api/events:search — multi-camera event list.
   *
   * Note the colon in the path. The BFF route is intentionally non-RESTful
   * (treating "search" as a verb) to avoid a GET with a giant query string.
   */
  async search(
    body: EventSearchRequest,
    signal?: AbortSignal,
  ): Promise<EventSearchResponse> {
    return api.post<EventSearchResponse>("/api/events:search", body, { signal });
  },
};
